import './kitStyles';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { flFix, tipPlace } from './floatPlace';
import { swallowClick } from './popRules';
import { useGhost } from './useGhost';

/**
 * WEB-3310 юнит 2а-1 — подсказка отдельным слоем вокруг любого элемента, эталон
 * `Tip({ tip, children, className, ...rest })` (`design/cabinet-rescin/kit.jsx`).
 * Обёртка `span[data-tiphost]`; при наведении мышью и фокусе с клавиатуры
 * (`:focus-visible`) — `.help-pop role=tooltip` порталом в ближайший
 * `dialog[open]` (окно лежит в верхнем слое браузера), иначе в ближайший `.ds`
 * (там шрифт и цвета кабинета). Сначала невидимо за экраном для замера, потом
 * место — снизу, нет места — сверху, по бокам 8 px (`tipPlace`) — и поправка
 * `flFix` у предка с трансформацией. На касании (`pointerType !== 'mouse'`)
 * касание открывает и закрывает, прокрутка любого слоя и касание мимо закрывают.
 * Эталон 24.10: открытая не открывается заново; первые 400 мс после открытия
 * касание по хозяину и прокрутка её не закрывают (щелчок и касание мимо —
 * закрывают); показ по фокусу — только если последним было не касание.
 * Закрытие плавное — `useGhost`.
 */
export interface TipProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  tip: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

interface At {
  r: DOMRect;
  x: number;
  y: number;
}

const OFF = -9999;

export function Tip({ tip, children, className, ...rest }: TipProps): React.ReactElement {
  const ref = useRef<HTMLSpanElement>(null);
  const pop = useRef<HTMLSpanElement>(null);
  const [at, setAtS] = useState<At | null>(null);
  // открытая подсказка не открывается заново: на iPhone за касанием идут фокус и мышиные события — мигала (эталон 24.10)
  const isOpen = useRef(false);
  const openedAt = useRef(0);
  const setAt = (v: At | null) => {
    if (!v || v.x === OFF) {
      isOpen.current = !!v;
      if (v) openedAt.current = Date.now();
    }
    setAtS(v);
  };
  // первые 400 мс после открытия касание, прокрутка от прячущейся панели Safari и повтор события не закрывают
  const fresh = () => Date.now() - openedAt.current < 400;
  const last = useRef<At | null>(null);
  if (at) last.current = at;
  const open = !!at;
  const { shown, ghost } = useGhost(open);
  const show = () => {
    if (isOpen.current) return;
    if (ref.current) setAt({ r: ref.current.getBoundingClientRect(), x: OFF, y: OFF });
  };

  useLayoutEffect(() => {
    if (!at || at.x !== OFF || !pop.current) return;
    const p = tipPlace(at.r, pop.current.offsetWidth, pop.current.offsetHeight, window.innerWidth, window.innerHeight);
    setAt({ r: at.r, ...p });
  }, [at]);
  useLayoutEffect(() => {
    if (at && at.x !== OFF && pop.current) flFix(pop.current, at.x, at.y);
  }, [at]);

  const touch = useRef(false);
  useEffect(() => {
    if (!open) return undefined;
    const off = () => {
      if (!fresh()) setAt(null);
    };
    // касание мимо только закрывает: щелчок до страницы не доходит (эталон kit.jsx:962–967)
    const out = (e: Event) => {
      if (!ref.current || ref.current.contains(e.target as Node)) return;
      if ((e as PointerEvent).pointerType !== 'mouse') swallowClick();
      setAt(null);
    };
    window.addEventListener('scroll', off, true);
    document.addEventListener('pointerdown', out, true);
    return () => {
      window.removeEventListener('scroll', off, true);
      document.removeEventListener('pointerdown', out, true);
    };
  }, [open]);

  const p = at || last.current;
  const box =
    shown && p && ref.current
      ? ref.current.closest('dialog[open]') || ref.current.closest('.ds') || document.body
      : null;
  return (
    <span
      ref={ref}
      className={className}
      data-tiphost=""
      onPointerDown={(e) => {
        touch.current = e.pointerType !== 'mouse';
        if (touch.current) {
          if (open) {
            if (!fresh()) setAt(null);
          } else show();
        }
      }}
      onMouseEnter={() => {
        if (!touch.current) show();
      }}
      onMouseLeave={() => {
        if (!touch.current) setAt(null);
      }}
      onFocus={(e) => {
        if (!touch.current && e.target.matches(':focus-visible')) show();
      }}
      onBlur={() => setAt(null)}
      {...rest}
    >
      {children}
      {box &&
        p &&
        createPortal(
          <span
            ref={pop}
            className="help-pop"
            role="tooltip"
            data-ghost={ghost ? '' : undefined}
            aria-hidden={ghost || undefined}
            style={{ left: p.x, top: p.y, opacity: p.x === OFF ? 0 : 1 }}
          >
            {tip}
          </span>,
          box,
        )}
    </span>
  );
}
